<template>
  <div>
    <section
      v-if="editor && editMode"
      class="buttons"
    >
      <v-btn-toggle
        v-model="toggle_multiple"
        style="display: flex; flex-wrap: wrap;"
        color="#8486D3"
        multiple
        @change="test"
      >
        <v-menu
          open-on-hover
          offset-y
        >
          <template #activator="{ on, attrs }">
            <v-btn
              color="#f5f5f5"
              dark
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>mdi-format-header-1</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="(item, index) in 3"
              :key="index"
              dense
              @click="setHeader(index + 1)"
            >
              <v-list-item-title>
                <v-icon :color="getActiveHeaderLevel() == index + 1 ? '#4CAF50' : null">
                  mdi-format-header-{{ index + 1 }}
                </v-icon>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        
        <v-btn
          :class="{ 'v-btn--active': editor.isActive('paragraph') }"
          :disabled="!editor.can().chain().focus().setParagraph().run()"
          @click="editor.chain().focus().setParagraph().run()"
        >
          <v-icon>mdi-format-paragraph</v-icon>
        </v-btn>

        <v-menu
          open-on-hover
          offset-y
        >
          <template #activator="{ on, attrs }">
            <v-btn
              color="#f5f5f5"
              dark
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>mdi-format-size</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="(fontsize, idx) in fontSizes"
              :key="idx"
              dense
              color="#4CAF50"
              @click="chooseFontSize(fontsize)"
            >
              <v-list-item-title :style="{color: getActiveFontSize(fontsize) ? '#8486D3' : null }">
                {{ fontsize }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-menu
          open-on-hover
          offset-y
        >
          <template #activator="{ on, attrs }">
            <v-btn
              color="#f5f5f5"
              dark
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>mdi-palette-outline</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="(color, idx) in fontColors"
              :key="idx"
              dense
              color="#4CAF50"
              @click="editor.chain().focus().setColor(color.code).run()"
            >
              <v-list-item-title :style="{color: color.code}">
                {{ color.name }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-btn
          :class="{ 'v-btn--active': editor.isActive('bold') }"
          :disabled="!editor.can().chain().focus().toggleBold().run()"
          @click="editor.chain().focus().toggleBold().run()"
        >
          <v-icon>mdi-format-bold</v-icon>
        </v-btn>

        <v-btn
          :class="{ 'v-btn--active': editor.isActive('italic') }"
          :disabled="!editor.can().chain().focus().toggleItalic().run()"
          @click="editor.chain().focus().toggleItalic().run()"
        >
          <v-icon>mdi-format-italic</v-icon>
        </v-btn>

        <v-btn
          :class="{ 'v-btn--active': editor.isActive('underline') }"
          :disabled="!editor.can().chain().focus().toggleUnderline().run()"
          @click="editor.chain().focus().toggleUnderline().run()"
        >
          <v-icon>mdi-format-underline</v-icon>
        </v-btn>
        
        <input 
          ref="uploader" 
          style="display: none;" 
          type="file"
          accept="image/png,image/jpeg,image/bmp,image/gif,image/webp"
          @change="onFileChanged"
        >
        <v-btn
          @click="handleFileImport"
        >
          <v-icon>mdi-image-outline</v-icon>
        </v-btn>
        
        <v-menu
          open-on-hover
          offset-y
        >
          <template #activator="{ on, attrs }">
            <v-btn
              color="#f5f5f5"
              dark
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>mdi-table</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="(tableItem, idx) in tableActions"
              :key="idx"
              dense
              color="#4CAF50"
              @click="tableItem.action(editor)"
            >
              <v-list-item-title>
                {{ tableItem.name }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        
        <v-btn
          :class="{ 'v-btn--active': editor.isActive('link')}"
          @click="setLink"
        >
          <v-icon>mdi-link-variant</v-icon>
        </v-btn>

        <v-btn
          :class="{ 'v-btn--active': editor.isActive({ textAlign: 'left' }) }"
          @click="editor.chain().focus().setTextAlign('left').run()"
        >
          <v-icon>mdi-format-align-left</v-icon>
        </v-btn>

        <v-btn
          :class="{ 'v-btn--active': editor.isActive({ textAlign: 'center' }) }"
          @click="editor.chain().focus().setTextAlign('center').run()"
        >
          <v-icon>mdi-format-align-center</v-icon>
        </v-btn>

        <v-btn
          :class="{ 'v-btn--active': editor.isActive({ textAlign: 'right' }) }"
          @click="editor.chain().focus().setTextAlign('right').run()"
        >
          <v-icon>mdi-format-align-right</v-icon>
        </v-btn>

        <v-btn
          :class="{ 'v-btn--active': editor.isActive('bulletList') }"
          @click="editor.chain().focus().toggleBulletList().run()"
        >
          <v-icon>mdi-format-list-bulleted</v-icon>
        </v-btn>

        <v-btn
          :class="{ 'v-btn--active': editor.isActive('orderedList') }"
          @click="editor.chain().focus().toggleOrderedList().run()"
        >
          <v-icon>mdi-format-list-numbered</v-icon>
        </v-btn>

        <v-btn
          :disabled="!editor.can().liftListItem('listItem')"
          @click="editor.chain().focus().liftListItem('listItem').run()"
        >
          <v-icon>mdi-receipt-text-arrow-left-outline</v-icon>
        </v-btn>
        
        <v-btn
          :disabled="!editor.can().sinkListItem('listItem')"
          @click="editor.chain().focus().sinkListItem('listItem').run()"
        >
          <v-icon>mdi-receipt-text-arrow-right-outline</v-icon>
        </v-btn>

        <v-btn
          @click="editor.chain().focus().unsetAllMarks().run()"
        >
          <v-icon>mdi-format-clear</v-icon>
        </v-btn>
        <v-btn
          :disabled="!editor.can().chain().focus().undo().run()"
          @click="editor.chain().focus().undo().run()"
        >
          <v-icon>mdi-undo</v-icon>
        </v-btn>
        <v-btn
          :disabled="!editor.can().chain().focus().redo().run()"
          @click="editor.chain().focus().redo().run()"
        >
          <v-icon>mdi-redo</v-icon>
        </v-btn>
      </v-btn-toggle>
      <!--
      <button @click="editor.chain().focus().setParagraph().run()" :class="{ 'is-active': editor.isActive('paragraph') }">
        paragraph
      </button>
      <button @click="editor.chain().focus().toggleBlockquote().run()" :class="{ 'is-active': editor.isActive('blockquote') }">
        blockquote
      </button> -->
    </section>
    <editor-content :editor="editor" />
  </div>
</template>

<script>
import axios from 'axios'
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import CodeBlock from '@tiptap/extension-code-block'
import TextStyle from '@tiptap/extension-text-style'
import TextAlign from '@tiptap/extension-text-align'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import ListItem from '@tiptap/extension-list-item'
import OrderedList from '@tiptap/extension-ordered-list'
import BulletList from '@tiptap/extension-bullet-list'
import Table from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import Placeholder from '@tiptap/extension-placeholder'
import FileHandler from '@tiptap/extension-file-handler'

import {Paragraph} from '@/assets/custom-tiptap-nodes/paragraph.ts'
import {CHeading} from '@/assets/custom-tiptap-nodes/heading.ts'
import FontSize from 'tiptap-extension-font-size'
import {CustomImage} from '@/assets/custom-tiptap-nodes/image.ts'
import { getFileUrl } from "@/assets/helpers/getFileUrl";

export default {
  components: {
    EditorContent,
  },
  props: {
    value: {
      type: String,
      required: true,
    },
    editMode: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      editor: null,
      toggle_multiple: [],
      fontSizes: ['По умолчанию', 8, 10, 12, 14, 16, 18, 20, 24, 30, 36, 48],
      fontColors: [
        {
          name: 'Черный',
          code: '#000000DE'
        },
        {
          name: 'Серый',
          code: '#A9A9A9'
        },
        {
          name: 'Красный',
          code: '#FF0000'
        },
        {
          name: 'Зеленый',
          code: '#4CAF50'
        },
        {
          name: 'Синий',
          code: '#0000FF'
        },
        {
          name: 'Желтый',
          code: '#FFD700'
        },
      ],
      tableActions: [
        {
          name: 'Вставить таблицу',
          action: (editor) => {editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
        },
        {
          name: 'Добавить столбец слева',
          action: (editor) => {editor.chain().focus().addColumnBefore().run()}
        },
        {
          name: 'Добавить столбец справа',
          action: (editor) => {editor.chain().focus().addColumnAfter().run()}
        },
        {
          name: 'Удалить столбец',
          action: (editor) => {editor.chain().focus().deleteColumn().run()}
        },
        {
          name: 'Добавить строку сверху',
          action: (editor) => {editor.chain().focus().addRowBefore().run()}
        },
        {
          name: 'Добавить строку снизу',
          action: (editor) => {editor.chain().focus().addRowAfter().run()}
        },
        {
          name: 'Удалить строку',
          action: (editor) => {editor.chain().focus().deleteRow().run()}
        },
        {
          name: 'Сделать заголовком',
          action: (editor) => {editor.chain().focus().toggleHeaderCell().run()}
        },
        {
          name: 'Объединить ячейки',
          action: (editor) => {editor.chain().focus().mergeCells().run()}
        },
        {
          name: 'Разделить ячейки',
          action: (editor) => {editor.chain().focus().splitCell().run()}
        },
        {
          name: 'Удалить таблицу',
          action: (editor) => {editor.chain().focus().deleteTable().run()}
        },
      ],
    }
  },
  watch: {
    value(value) {
      // HTML
      const isSame = this.editor.getHTML() === value

      // JSON
      // const isSame = JSON.stringify(this.editor.getJSON()) === JSON.stringify(value)

      if (isSame) {
        return
      }

      this.editor.commands.setContent(value, false)
      this.$emit('input', this.editor.getHTML())
    },
    editMode(value) {
      this.editor.setEditable(value)
    },
  },
  mounted() {
    this.editor = new Editor({
      editorProps: {
        attributes: {
          class: 'tiptap-styles',
          spellcheck: false,
        }
      },
      content: this.value,
      editable: this.editMode,
      extensions: [
        StarterKit.configure({
          paragraph: false,
          codeBlock: false,
          listItem: false,
          orderedList: false,
          bulletList: false,
        }),
        Paragraph,
        CHeading.configure({
          levels: [1, 2, 3]
        }),
        Link.configure({
          openOnClick: false,
        }),
        Underline,
        CodeBlock,
        TextStyle,
        FontSize,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        Color,
        Highlight.configure({
          multicolor: true
        }),
        ListItem,
        OrderedList,
        BulletList,
        Table.configure({
            HTMLAttributes: {
                style: `border-collapse: collapse; table-layout: fixed; width: 100%; margin: 0; overflow: hidden;`
            },
        }),
        TableCell.configure({
            HTMLAttributes: {
                style: `min-width: 1em; border: 2px solid #ced4da; padding: 3px 5px; vertical-align: top; box-sizing: border-box; position: relative;`
            },
        }),
        TableHeader.configure({
            HTMLAttributes: {
                style: `min-width: 1em; border: 2px solid #ced4da; padding: 3px 5px; vertical-align: top; box-sizing: border-box; position: relative;font-weight: bold; text-align: left; background-color: #f1f3f5; min-height: 48px;`
            },
        }),
        TableRow,
        Placeholder.configure({
          // Use a placeholder:
          placeholder: 'Введите текст',
          showOnlyWhenEditable: false,
          // Use different placeholders depending on the node type:
          // placeholder: ({ node }) => {
          //   if (node.type.name === 'heading') {
          //     return 'What’s the title?'
          //   }

          //   return 'Can you add some further context?'
          // },
        }),
        CustomImage.configure({
          inline: true,
          allowBase64: true
        }),
        FileHandler.configure({
          allowedMimeTypes: ['image/jpeg', 'image/png'],
          onPaste: async (currentEditor, files, htmlContent) => {
            if (!files.length) return
            const formData = new FormData()
            files.forEach(file => {
              if (htmlContent) {
                // if there is htmlContent, stop manual insertion & let other extensions handle insertion via inputRule
                // you could extract the pasted file from this url string and upload it to a server for example
                console.log(htmlContent) // eslint-disable-line no-console
                return false
              }
              formData.append('file', file)
            })
            await axios.post('/files/path/articles/images', formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              },
            })
              .then(response => {
                response.data.forEach((image, idx) => {
                  const imageURL = getFileUrl(image)
                    let reader = new FileReader();
                    reader.readAsDataURL(files[idx]);
                    reader.onload = e => {
                        const src = e.target.result;
                        const img = new Image();
                        img.onload = async () => {
                            this.editor.commands.setImage({src: imageURL, width: img.width, height: img.height})
                        };
                        img.onerror = (e) => {
                            console.log(e);
                        };
                        img.src = src;
                    };
                })
              })
          },
          onDrop: async (currentEditor, files) => {
            if (!files.length) return
            const formData = new FormData()
            files.forEach(file => {
              formData.append('file', file)
            })
            await axios.post('/files', formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              },
            })
                .then(response => {
                    response.data.forEach((image, idx) => {
                        const imageURL = getFileUrl(image)
                        let reader = new FileReader();
                        reader.readAsDataURL(files[idx]);
                        reader.onload = e => {
                            const src = e.target.result;
                            const img = new Image();
                            img.onload = async () => {
                                this.editor.commands.setImage({src: imageURL, width: img.width, height: img.height})
                            };
                            img.onerror = (e) => {
                                console.log(e);
                            };
                            img.src = src;
                        };
                    })
                })
          },
        })
      ],
      onUpdate: () => {
        this.$emit('input', this.editor.getHTML())
      },
    })
  },
  beforeDestroy() {
    this.editor.destroy()
  },
  methods: {
    toggleResize() {
			this.editor.chain().focus().toggleResizable().run();
		},
    test() {
      this.toggle_multiple = []
    },
    getRandomId(length = 20) {
      return (Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2)).substring(0, length);
    },
    getRandomInteger(min, max) {
      let rand = min + Math.random() * (max + 1 - min);
      return Math.floor(rand);
    },
    setHeader(level = 1) {
      this.editor.chain().focus().unsetFontSize().run()
      const id = this.editor.state.selection.$head.parent.attrs.id
      this.editor.chain().focus().toggleHeading({ level: level }).run()
      let nodeType = this.editor.state.selection.$head.parent.type.name
      if (id) {
        this.editor.commands.updateAttributes(nodeType, {id: id})
      }
    },
    chooseFontSize(fontsize) {
      if (this.editor.state.selection.$head.parent.type.name === 'heading') {
        return
      }
      if (fontsize === 'По умолчанию') {
        return this.editor.chain().focus().unsetFontSize().run()
      }
      this.editor.chain().focus().setFontSize(fontsize + 'px').run()
    },
    setLink() {
      const previousUrl = this.editor.getAttributes('link').href
      const url = window.prompt('URL', previousUrl)

      // cancelled
      if (url === null) {
        return
      }

      // empty
      if (url === '') {
        this.editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .unsetLink()
          .run()

        return
      }

      // update link
      this.editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url })
        .run()
    },
    getActiveHeaderLevel() {
      if (this.editor.isActive('heading', { level: 1})) {
        return 1
      }
      if (this.editor.isActive('heading', { level: 2})) {
        return 2
      }
      if (this.editor.isActive('heading', { level: 3})) {
        return 3
      }
      return -1
    },
    getActiveFontSize(fontsize) {
        if (this.editor.getAttributes('textStyle')?.fontSize && !this.editor.isActive('heading')) {
            if (fontsize === 'По умолчанию') {
                fontsize = 16
            }
            return this.editor.getAttributes('textStyle')?.fontSize.replace(/[^0-9]+/g, '') == fontsize
        }
    //   return false
    },
    handleFileImport() {
      this.$refs.uploader.click()
    },
    async onFileChanged(e) {
        const file = e.target?.files[0]
        if (!file) return
        let reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = e => {
            const src = e.target.result;
            const img = new Image();
            img.onload = async () => {
                const formData = new FormData()
                formData.append('file', file)
                await axios.post('/files', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                })
                    .then(response => {
                        const imageURL = getFileUrl(response.data[0])
                        this.editor.commands.setImage({src: imageURL, width: img.width, height: img.height})
                    })
            };
            img.onerror = (e) => {
                console.log(e);
            };
            img.src = src;
        };
    },
  },
}
</script>

<style lang="scss">
.tiptap-styles {
  padding: 8px;
  border-radius: 3px;
  font-family: 'Open Sans';
  outline-width: 0;
}
// TODO may affect other things
html {
  scroll-padding-top: 120px;
}
// :target {
//             border: 2px solid #D4D4D4;
//             background-color: rgb(59, 185, 47);
//             color: white;
//          }
</style>
<style lang="scss" scoped>
.buttons {
  display: flex;
  justify-content: center;
  margin-bottom: 5px;
  border-bottom: 1px solid #A6ADB5;
  border-radius: 3px;
  padding: 15px;
  position: sticky !important;
  top: -20px;
  background: $white;
  z-index: 1;
}
</style>
<style lang="scss">
.tiptap {
  table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    margin: 0;
    overflow: hidden;

    .selectedCell:after {
      z-index: 2;
      position: absolute;
      content: "";
      left: 0; right: 0; top: 0; bottom: 0;
      background: rgb(200, 200, 255, 0.4);
      pointer-events: none;
    }

    .column-resize-handle {
      position: absolute;
      right: -2px;
      top: 0;
      bottom: -2px;
      width: 4px;
      background-color: #adf;
      pointer-events: none;
    }

    p {
      margin: 0;
    }
  }

  pre {
    background: #0D0D0D;
    color: #FFF;
    font-family: 'JetBrainsMono', monospace;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;

    code {
      color: inherit;
      padding: 0;
      background: none;
      font-size: 0.8rem;
    }
  }
}

.tableWrapper {
  padding: 1rem 0;
  overflow-x: auto;
}

.resize-cursor {
  cursor: ew-resize;
  cursor: col-resize;
}
.tiptap p.is-editor-empty:first-child::before {
  color: #adb5bd;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}
</style>